import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { mergeMap, take, catchError, filter, switchMap } from 'rxjs/operators';
import { AmplifyService } from '@app/core/infrastructure/auth/amplify.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private authAmplifyService: AmplifyService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip auth for public endpoints
    if (!request.url.includes('/protected/')) {
      return next.handle(request);
    }

    return this.authAmplifyService.getIdToken().pipe(
      take(1),
      mergeMap(token => {
        if (token) {
          request = this.addTokenToRequest(request, token);
        }
        return next.handle(request).pipe(
          catchError((error: HttpErrorResponse) => {
            // If we get a 401, try to refresh the token once
            if (error.status === 401 && !request.url.includes('/auth/')) {
              return this.handle401Error(request, next);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private addTokenToRequest(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // Try to get a fresh token
      return this.authAmplifyService.refreshSession().pipe(
        switchMap((token: string | null) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);
          
          if (token) {
            // Retry the request with the new token
            return next.handle(this.addTokenToRequest(request, token));
          }
          
          // If refresh failed, the error interceptor will handle it
          return throwError(() => new Error('Session refresh failed'));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          // Let the error interceptor handle the redirect
          return throwError(() => err);
        })
      );
    } else {
      // Wait for the token refresh to complete
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(this.addTokenToRequest(request, token!));
        })
      );
    }
  }
}
